import { useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import {
  ChecklistFlowchart,
  ConnectionsFlowchart,
  JourneyFlowchart,
  PathFlowchart,
} from "../../components/admin/SitemapFlowcharts";
import {
  ADMIN_ROUTES,
  AGENT_ROUTES,
  BROKER_ROUTES,
  BUYER_PATH,
  CHECKLIST_LOCAL,
  CHECKLIST_PUBLIC,
  CLIENT_MSG,
  CROSS_LINKS,
  JOURNEY_LOCAL,
  JOURNEY_NAV,
  JOURNEY_ROUTES,
  SELLER_PATH,
  SELLER_REDIRECTS,
} from "../../js/api/siteMap";
import { useAuth } from "../../js/hooks/useAuth";
import { betaOnboardingUrl, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";

const TABS = [
  { id: "connect", label: "Connections" },
  { id: "journey", label: "Journey hub" },
  { id: "checklist", label: "REChecklist" },
];

function RouteTable({ rows, pathKey = "path", pageKey = "page", notesKey = "notes" }) {
  const hasNotes = rows.some((row) => row[notesKey]);
  return (
    <table className="admin-table admin-table--static">
      <thead>
        <tr>
          <th>Path</th>
          <th>Page</th>
          {hasNotes ? <th>Notes</th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[pathKey]}>
            <td>
              <code>{row[pathKey]}</code>
            </td>
            <td>{row[pageKey]}</td>
            {hasNotes ? <td className="muted">{row[notesKey] || ""}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AdminSitemap() {
  const { session } = useAuth();
  const [tab, setTab] = useState("connect");

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Site Map</h1>
          <p className="page-lead">
            Two origins, one product. REChecklist.pro is the working app. DreamHomeJourney.online is
            sales, support, billing, and the public hubs. They do not share a live database.
          </p>
        </div>
      </div>

      <nav className="admin-tabs" aria-label="Site map sections">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`messages-tab${tab === item.id ? " is-active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === "connect" ? <ConnectionsPanel /> : null}
      {tab === "journey" ? <JourneyPanel /> : null}
      {tab === "checklist" ? <ChecklistPanel /> : null}
    </AppLayout>
  );
}

function ConnectionsPanel() {
  return (
    <>
      <section className="desk-panel card">
        <h2>How the two sites connect</h2>
        <p className="muted">
          Footer Support keeps pointing at the Journey site with <code>role</code> / <code>user</code>{" "}
          query params. Admin Controls writes the <code>@hub</code> pack on this origin. Journey reads
          the same seed on its origin until a later import.
        </p>
        <ConnectionsFlowchart />
        <p className="muted sitemap-local">
          Local: app <code>{CHECKLIST_LOCAL}</code> · hub <code>{JOURNEY_LOCAL}</code>
        </p>
      </section>

      <section className="desk-panel card">
        <h2>Live links today</h2>
        <table className="admin-table admin-table--static">
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th>What travels</th>
            </tr>
          </thead>
          <tbody>
            {CROSS_LINKS.map((row) => (
              <tr key={row.from}>
                <td>{row.from}</td>
                <td>
                  <code>{row.to}</code>
                </td>
                <td className="muted">{row.travels}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted" style={{ marginTop: "0.85rem" }}>
          Not connected yet: live billing, mail, or a shared store between origins. Password reset
          and broadcasts stay local on this desk.
        </p>
      </section>
    </>
  );
}

function JourneyPanel() {
  return (
    <>
      <section className="desk-panel card">
        <h2>DreamHomeJourney.online</h2>
        <p className="muted">
          Header: {JOURNEY_NAV.header.join(" · ")}. Footer: {JOURNEY_NAV.footer.join(" · ")}.{" "}
          <code>?role=</code> and <code>?user=</code> copy across header and footer when present.
        </p>
        <JourneyFlowchart />
      </section>
      <section className="desk-panel card">
        <h2>Journey routes</h2>
        <RouteTable rows={JOURNEY_ROUTES} />
        <p className="muted" style={{ marginTop: "0.85rem" }}>
          Content for these pages is edited on this desk under{" "}
          <Link to="/admin/controls">Controls</Link>. Buyer and seller help stay on their own carpets.
        </p>
      </section>
    </>
  );
}

function ChecklistPanel() {
  return (
    <>
      <section className="desk-panel card">
        <h2>REChecklist.pro</h2>
        <p className="muted">
          Public header: Home · Main Dashboard · Buyer Journey · Seller Journey · Notices. Footer on
          every page includes Support (Journey), Contact, Privacy, Terms, Notices, and both domain
          names.
        </p>
        <ChecklistFlowchart />
      </section>

      <section className="desk-panel card">
        <h2>Public and account</h2>
        <RouteTable rows={CHECKLIST_PUBLIC} />
      </section>

      <section className="desk-panel card">
        <h2>Buyer journey</h2>
        <p className="muted">Own state — never mixed with seller.</p>
        <PathFlowchart title="Buyer path" steps={BUYER_PATH} tone="buyer" />
      </section>

      <section className="desk-panel card">
        <h2>Seller journey</h2>
        <p className="muted">Own state — never mixed with buyer.</p>
        <PathFlowchart title="Seller path" steps={SELLER_PATH} tone="seller" />
        <p className="muted">Redirects</p>
        <ul className="sitemap-redirects">
          {SELLER_REDIRECTS.map((item) => (
            <li key={item.path}>
              <code>{item.path}</code> → <code>{item.to}</code>
            </li>
          ))}
        </ul>
      </section>

      <section className="desk-panel card">
        <h2>Client messaging</h2>
        <p className="muted">Buyers and sellers only. Admin is sent to /admin/messages.</p>
        <p>
          {CLIENT_MSG.map((path) => (
            <code key={path} className="sitemap-code-gap">
              {path}
            </code>
          ))}
        </p>
      </section>

      <section className="desk-panel card">
        <h2>Agent desk</h2>
        <RouteTable rows={AGENT_ROUTES} />
      </section>

      <section className="desk-panel card">
        <h2>Broker desk</h2>
        <p className="muted">Same shape as agent, plus profile and retention. My Business vs All Agents stays on this desk.</p>
        <RouteTable rows={BROKER_ROUTES} />
      </section>

      <section className="desk-panel card">
        <h2>Admin desk</h2>
        <p className="muted">
          Manages Journey content. Does not fork the messaging inbox. Desk previews use{" "}
          <code>?as=userId</code>.
        </p>
        <RouteTable rows={ADMIN_ROUTES} />
        <p className="row" style={{ gap: "0.65rem", flexWrap: "wrap", marginTop: "0.85rem" }}>
          <Link className="ghost-btn" to="/admin/messages">
            Messages
          </Link>
          <Link className="ghost-btn" to="/admin/suggestions">
            Suggestions
          </Link>
          <Link className="ghost-btn" to="/admin/beta">
            Beta feedback
          </Link>
          <a className="ghost-btn" href={betaOnboardingUrl(session)} {...JOURNEY_LINK_PROPS}>
            Beta Onboarding Preview
          </a>
          <Link className="ghost-btn" to="/admin/users">
            Users
          </Link>
          <Link className="ghost-btn" to="/admin/controls">
            Controls
          </Link>
        </p>
      </section>
    </>
  );
}
